/**
 * Signature + per-tool auto-approve settings, forms via tanstack-react-form (no useState).
 * Destructive tools are auto-approvable too (red badge only) — operator owns the risk.
 */

import { useForm } from "@tanstack/react-form";
import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useMutation, useQuery } from "convex/react";
import { ChevronDownIcon } from "lucide-react";
import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
	FieldSet,
} from "@/components/ui/field";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SignaturePasteEditor } from "@/components/ui/signature-paste-editor";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import type { ComposeBodyStyle } from "@/lib/sanitize-clipboard-html";
import { api } from "../../../convex/_generated/api";

// Email signature card — lifted from EmailSignaturePage, migrated off useState+useEffect

function SignatureCard() {
	const data = useQuery(api.users.getMySignature);
	const updateSignature = useMutation(api.users.updateMySignature);

	const { mutateAsync: doSave, isPending: saving } = useReactQueryMutation({
		mutationFn: async (args: {
			signatureHtml: string;
			composeBodyStyle: ComposeBodyStyle | null;
		}) => {
			await updateSignature({
				signatureHtml: args.signatureHtml,
				...(args.composeBodyStyle
					? { composeBodyStyle: args.composeBodyStyle }
					: {}),
			});
		},
	});

	const form = useForm({
		// Tanstack form re-reads defaults when this object's reference changes,
		// so the form "rebases" whenever the convex query result updates.
		defaultValues: {
			signatureHtml: data?.signatureHtml ?? "",
			composeStyle: (data?.composeBodyStyle ?? null) as ComposeBodyStyle | null,
		},
		onSubmit: async ({ value }) => {
			await doSave({
				signatureHtml: value.signatureHtml,
				composeBodyStyle: value.composeStyle,
			});
			form.reset({
				signatureHtml: value.signatureHtml,
				composeStyle: value.composeStyle,
			});
		},
	});

	if (data === undefined) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>Email signature</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<Skeleton className="h-40 w-full" />
					<div className="flex items-center justify-between gap-2">
						<Skeleton className="h-8 w-16" />
						<div className="flex items-center gap-2">
							<Skeleton className="h-8 w-20" />
							<Skeleton className="h-8 w-32" />
						</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>Email signature</CardTitle>
				<CardDescription>
					Your signature is appended to email drafts created by the Clerical
					agent. Open a new email in Outlook so your signature appears, select
					it, copy, and paste it into the box below. Fonts, links, and images
					come through as-is.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						form.handleSubmit();
					}}
				>
					<form.Field name="signatureHtml">
						{(field) => (
							<form.Field name="composeStyle">
								{(styleField) => (
									<FieldGroup>
										<Field>
											<SignaturePasteEditor
												value={field.state.value}
												onChange={field.handleChange}
												onComposeBodyStyleDetected={(style) => {
													styleField.handleChange(style);
												}}
											/>
											{styleField.state.value ? (
												<FieldDescription>
													Detected compose font:{" "}
													<span className="font-mono text-foreground">
														{styleField.state.value.fontFamily} ·{" "}
														{styleField.state.value.fontSize}
													</span>{" "}
													— used to style the body of agent drafts.
												</FieldDescription>
											) : null}
										</Field>
										<div className="flex items-center justify-between gap-2">
											<Button
												type="button"
												variant="ghost"
												size="sm"
												onClick={() => {
													field.handleChange("");
													styleField.handleChange(null);
												}}
												disabled={saving || field.state.value.length === 0}
											>
												Clear
											</Button>
											<div className="flex items-center gap-2">
												<Button
													type="button"
													variant="ghost"
													onClick={() => form.reset()}
													disabled={saving || !form.state.isDirty}
												>
													Revert
												</Button>
												<form.Subscribe selector={(s) => s.isDirty}>
													{(isDirty) => (
														<Button type="submit" disabled={saving || !isDirty}>
															{saving ? "Saving..." : "Save signature"}
														</Button>
													)}
												</form.Subscribe>
											</div>
										</div>
									</FieldGroup>
								)}
							</form.Field>
						)}
					</form.Field>
				</form>
			</CardContent>
		</Card>
	);
}

function AutoApprovedToolsCard() {
	const perms = useQuery(api.users.getMyPermissions);
	const tools = useQuery(api.users.listAutoApprovableTools);
	const toggleTool = useMutation(api.users.toggleAutoApproveTool);

	const enabled = useMemo(
		() => new Set(perms?.autoApproveTools ?? []),
		[perms?.autoApproveTools],
	);

	const grouped = useMemo(() => {
		type ToolMeta = NonNullable<typeof tools>[number];
		if (!tools) return [] as Array<{ group: string; items: ToolMeta[] }>;
		const map = new Map<string, ToolMeta[]>();
		for (const t of tools) {
			const existing = map.get(t.group) ?? [];
			existing.push(t);
			map.set(t.group, existing);
		}
		return Array.from(map.entries()).map(([group, items]) => ({
			group,
			items,
		}));
	}, [tools]);

	const toolMutation = useReactQueryMutation({
		mutationFn: async (args: { toolName: string; enabled: boolean }) => {
			await toggleTool(args);
		},
	});

	if (tools === undefined || perms === undefined) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>Auto-approved actions</CardTitle>
				</CardHeader>
				<CardContent className="space-y-5">
					{/* Mirror the live layout: four grouped sections */}
					{[0, 1, 2, 3].map((groupIdx) => (
						<div key={groupIdx} className="space-y-3 border-t pt-4">
							<Skeleton className="h-4 w-24" />
							{[0, 1, 2].map((rowIdx) => (
								<div key={rowIdx} className="flex items-center justify-between">
									<div className="flex-1 space-y-1.5">
										<Skeleton className="h-4 w-40" />
										<Skeleton className="h-3 w-32" />
									</div>
									<Skeleton className="h-6 w-10 rounded-full" />
								</div>
							))}
						</div>
					))}
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>Auto-approved actions</CardTitle>
				<CardDescription>
					Skip Clerical's confirmation step for trusted actions. Operator owns
					the risk — destructive actions (delete, send for signature) are
					auto-approvable too, with a red badge for visibility. Toggle off any
					time to restore the confirmation prompt.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<FieldGroup>
					{grouped.map(({ group, items }) => (
						<FieldSet key={group} className="gap-3 border-t pt-4">
							<legend className="font-medium text-sm">{group}</legend>
							{items.map((tool) => {
								const on = enabled.has(tool.name);
								return (
									<Field key={tool.name} orientation="horizontal">
										<div className="flex-1">
											<div className="flex items-center gap-2">
												<FieldLabel htmlFor={`tool-${tool.name}`}>
													{tool.label}
												</FieldLabel>
												{tool.destructive ? (
													<Badge variant="destructive">Destructive</Badge>
												) : null}
											</div>
											<FieldDescription>
												<span className="font-mono text-[10px]">
													{tool.name}
												</span>
												{tool.destructive ? (
													<>
														{" · "}
														<span>
															Permanently changes shared firm content.
														</span>
													</>
												) : null}
											</FieldDescription>
										</div>
										<Switch
											id={`tool-${tool.name}`}
											checked={on}
											onCheckedChange={(checked) =>
												toolMutation.mutate({
													toolName: tool.name,
													enabled: checked,
												})
											}
											disabled={toolMutation.isPending}
										/>
									</Field>
								);
							})}
						</FieldSet>
					))}
				</FieldGroup>
			</CardContent>
		</Card>
	);
}

export default function SettingsPage() {
	return (
		<div className="flex h-full flex-col">
			<header className="flex shrink-0 items-center border-b py-3 pl-14 pr-6">
				<h1 className="text-lg font-semibold">Settings</h1>
			</header>
			<ScrollArea className="min-h-0 flex-1">
				<div className="mx-auto flex max-w-4xl flex-col gap-6 p-6">
					<SignatureCard />
					<AutoApprovedToolsCard />
				</div>
			</ScrollArea>
		</div>
	);
}

// Reserved for a future searchable combobox picker.
void ChevronDownIcon;
